import { memo, useEffect, useRef, useState } from "react";
import { getAllData } from "../mediaCache";
import { AudioPreviews } from "./AudioPreview.jsx";
import { Documents } from "./Document.jsx";
import { ImagePreviews } from "./ImagePreview.jsx";
import { VideoPreviews } from "./VideoPreview.jsx";

const MEDIA_TABS = ["all", "image", "video", "document"];
const TYPE_OPTIONS = ["all", "incoming", "outgoing"];

const MediaTabs = memo(({ Contactinfo }) => {
  const [activeTab, setActiveTab] = useState("all");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const mediaRef = useRef([]);
  // Trigger re-render when mediaRef changes
  const [, forceUpdate] = useState(0);

  // FIXED: was depending on [Contactinfo, type, activeTab] which caused 3 separate
  // loads on open. Now Contactinfo triggers a full reload, while tab/direction filter
  // works on the already-fetched data without re-fetching.
  useEffect(() => {
    loadAllMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Contactinfo]);

  useEffect(() => {
    // Re-filter without network call when tab or direction changes
    forceUpdate(n => n + 1);
  }, [activeTab, directionFilter]);

  const loadAllMedia = async () => {
    setLoading(true);
    try {
      mediaRef.current = await getAllData();
    } finally {
      setLoading(false);
    }
  };

  // Apply filters in render (pure client-side, no fetch)
  const filteredMedia = mediaRef.current.filter(item => {
    const directionMatch =
      directionFilter === "all" ||
      item.key.startsWith(directionFilter);
    const typeMatch =
      activeTab === "all" ||
      item.value.type === activeTab;
    return directionMatch && typeMatch;
  });

  return (
    <>
      {/* Tab bar */}
      <ul className="nav nav-tabs mb-2 d-flex justify-content-between" role="tablist">
        {MEDIA_TABS.map(tab => (
          <li className="nav-item" key={tab} role="presentation">
            <button
              className={`nav-link ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
              style={{ textTransform: "capitalize" }}
              aria-selected={activeTab === tab}
              role="tab"
            >
              {tab}
            </button>
          </li>
        ))}
      </ul>

      {/* Direction filter */}
      <select
        value={directionFilter}
        onChange={(e) => setDirectionFilter(e.target.value)}
        className="px-2 py-1 mb-2"
        aria-label="Filter by direction"
        style={{
          width: "100%",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          fontSize: "14px",
          outline: "none",
          backgroundColor: "#fff",
          cursor: "pointer",
        }}
      >
        {TYPE_OPTIONS.map(opt => (
          <option key={opt} value={opt} style={{ textTransform: "capitalize" }}>
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </option>
        ))}
      </select>

      {/* Content */}
      {loading && <p className="text-center text-muted">Loading…</p>}

      {!loading && filteredMedia.length === 0 && (
        <p className="text-center text-muted">No media found</p>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "8px",
          maxHeight: "500px",
          width: "100%",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {filteredMedia.map(item => {
          // Enrich item for child components (non-mutating copy)
          const chat = {
            ...item.value,
            body_type: item.value.type,
            type: item.key.startsWith("outgoing") ? "outgoing" : "incoming",
          };

          switch (chat.body_type) {
            case "image":    return <ImagePreviews key={item.key} chat={chat} />;
            case "video":    return <VideoPreviews key={item.key} chat={chat} />;
            case "audio":    return <AudioPreviews key={item.key} chat={chat} />;
            case "document": return <Documents key={item.key} chat={chat} />;
            default:         return null;
          }
        })}
      </div>
    </>
  );
});

export default MediaTabs;